import { useState, useEffect, useLayoutEffect, useRef } from 'react';
import {
  FormHelperText,
  MenuItem,
  Dialog,
  Link,
  CardContent,
  InputLabel,
  Chip,
  Card,
  Select,
  Autocomplete,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  IconButton,
  SvgIcon,
  Grid,
  Checkbox,
  Stack,
  TextField,
  Typography,
  CircularProgress,
  useMediaQuery,
  Drawer,
  Tooltip
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useSnackbar } from 'components/snackbar';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, Controller } from 'react-hook-form';
import FormProvider from 'hooks/form/form-provider';
import { CandidateWorkHistorySchema, CandidateWorkHistoryForGapSchema } from 'models/CandidateProfileSchema';
import { updateCandidateWorkExperience, getWorkHistoryData, deleteWorkHistory } from 'api/users';
import dayjs from 'dayjs';
import DynamicTextField from 'components/DynamicTextField';
import TypeAhead from 'components/TypeAhead';
import { endpoints } from 'utils/axios';
import { styled } from '@mui/material/styles';
import Switch from '@mui/material/Switch';
import AddIcon from '@mui/icons-material/Add';
import { changeNameFormat, toTitleCase, allCapitalize } from 'utils/Utils';
import AddSkills from './AddSkills';
import KeyboardArrowUpOutlinedIcon from '@mui/icons-material/KeyboardArrowUpOutlined';
import RemoveWorkExperience from './RemoveWorkExperienceDialog';
import { cleanData } from 'data/city.js';
import Overlapping from './Overlapping';
import { useAuth } from 'hooks/useAuthContext';
import { workDefSkill } from 'data/language';
import { useTheme } from '@mui/material/styles';
import { DeleteSvg, EditSvgIcon } from 'components/icon/icon';
import { useBackNavigationDialog } from 'hooks/useBackNavigationDialog';

const AntSwitch = styled(Switch)(({ theme }) => ({
  width: 28,
  height: 16,
  padding: 0,
  top: 5,
  display: 'flex',
  '&:active': {
    '& .MuiSwitch-thumb': {
      width: 15,
    },
    '& .MuiSwitch-switchBase.Mui-checked': {
      transform: 'translateX(9px)',
    },
  },
  '& .MuiSwitch-switchBase': {
    padding: 2,
    '&.Mui-checked': {
      transform: 'translateX(12px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        opacity: 1,
        backgroundColor: theme.palette.mode === 'dark' ? '#177ddc' : '#1890ff',
      },
    },
  },
  '& .MuiSwitch-thumb': {
    boxShadow: '0 2px 4px 0 rgb(0 35 11 / 20%)',
    width: 12,
    height: 12,
    borderRadius: 6,
    transition: theme.transitions.create(['width'], {
      duration: 200,
    }),
  },
  '& .MuiSwitch-track': {
    borderRadius: 16 / 2,
    opacity: 1,
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,.35)' : 'rgba(0,0,0,.25)',
    boxSizing: 'border-box',
  },
}));

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  }
};


/**
 * AddWorkExperience is a React component that manages the addition and editing of a candidate's work experience.
 * 
 * @param {function} onCancel - Callback function to handle cancel action.
 * @param {function} onUploadSuccess - Callback function upon successful upload of work experience data.
 * @param {string} id - Identifier for the work experience entry, used to determine edit mode.
 * @param {object} workHistoryData - Data object containing details of the work experience.
 * @param {function} onWorkExperienceDataChange - Callback function to handle changes in work experience data.
 * @param {string} currentCompany - Name of the current company the user is working at.
 * @param {array} roleData - Array containing role-related data for job function selection.
 * @param {function} checkingOverlapping - Function to check for overlapping work experiences.
 * @param {boolean} showWork - Flag to determine whether to show work experience details.
 * @param {function} handleShowWork - Function to handle displaying work experience details.
 * @param {function} handleDeleteEdu - Function to handle deletion of education entries.
 * @param {function} handleArrowClick - Function to handle UI interactions with arrow clicks.
 * @param {number} workHistoryLength - Length of the work history array.
 * @param {boolean} isReadOnly - Flag to determine if the form is in read-only mode.
 * @param {function} setReadOnly - Function to toggle read-only state of the form.
 */

const AddWorkExperience = ({ onCancel, onUploadSuccess, id, workHistoryData, onWorkExperienceDataChange, currentCompany, roleData, checkingOverlapping, showWork, handleShowWork, handleDeleteEdu, handleArrowClick, workHistoryLength, isReadOnly, setReadOnly }) => {
  const theme = useTheme();
  const isEditMode = !!id;
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const [explaination, setExplnation] = useState(workHistoryData?.organization_name === "Work Gap" ? true : false);
  const [from, setFrom] = useState(dayjs(workHistoryData?.work_history_from) || null);
  const [showFlag, setShowFlag] = useState('');
  // const [present, setPresent] = useState(workHistoryData?.present ? true : false);
  const [keyAccomplishments, setKeyAccomplishments] = useState(
    workHistoryData?.key_accomplishments?.length > 0 ? [...workHistoryData?.key_accomplishments, ''] : ['']
  );
  const focusRef = useRef(null);
  // const [selectedChips, setSelectedChips] = useState(workHistoryData?.skills || []);
  // const [open, setOpen] = useState(false);
  // const [openRemove, setOpenRemove] = useState(false);
  const [error, setErrors] = useState(false);
  const [chipData, setChipData] = useState(workHistoryData?.skills?.length > 2 ? workHistoryData?.skills : workHistoryData?.all_skills || []);
  // const [addMoreSkills, setAddMoreSkills] = useState(workHistoryData?.all_skills || []);
  const [industryList, setIndustryList] = useState([]);
  const jobType = [{ label: 'Full time' }, { label: 'Part time' }, { label: 'Temporary' }, { label: 'Internship' }, { label: 'Trainee' }, { label: 'Freelancer (Self employed)' }];

  const defaultValues = {
    work_history_from: null,
    work_history_to: null,
    work_history_location: '',
    organization_name: '',
    organization_details: null,
    industry_sector: '',
    present: false,
    last_job_function: '',
    highest_designation: '',
    job_type: 'Full time',
    team_size_managed: 'Individual contributor (IC)',
    work_oriented_skills: [],
    gap_explaination: '',
    gap_title: '',
    show_in_resume: false,
    // key_accomplishments: [''],
    skills: [],
    all_skills: []
  };

  /**
   * Checks if all values in the given data object are empty.
   * A value is considered empty if it is null, undefined, an empty string, or an empty array.
   *
   * @param {object} data - The data object to check
   * @returns {boolean} True if all values in the data object are empty, false otherwise
   */

  const checkLocalDataIsEmpty = (data) => {
    if (!data) return true;

    const meaningfulFields = [
      'organization_name',
      'highest_designation',
      'last_job_function',
      'work_history_location',
      'work_history_from',
      'work_history_to',
      'skills',
      'key_accomplishments',
    ];

    const hasMeaningfulData = meaningfulFields.some(field => {
      const value = data[field];

      if (value === null || value === undefined) {
        return false;
      }

      if (Array.isArray(value)) {
        return value.filter(item => String(item).trim() !== '').length > 0;
      }

      if (typeof value === 'string') {
        return value.trim() !== '';
      }

      return true;
    });

    return !hasMeaningfulData;
  };

  const methods = useForm({
    defaultValues,
    mode: 'onChange',
    resolver: yupResolver(explaination ? CandidateWorkHistoryForGapSchema() : CandidateWorkHistorySchema(user?.date_of_birth)),
  });

  const {
    register,
    handleSubmit,
    control,
    setValue,
    getValues,
    reset,
    formState: { errors, isSubmitting, isDirty },
    watch,
    trigger,
    setError,
    clearErrors,
  } = methods;

  const present = watch('present');
  // const keyAccomplishments = watch('key_accomplishments', []);
  const selectedChips = watch('skills', []);

  useLayoutEffect(() => {
    if (!isEditMode) focusRef.current?.focus()
  }, []);

  // const [openOverlapping, setOpenOverlapping] = useState(false);
  const [type, setType] = useState('');
  const [degree, setDegree] = useState('');
  const [newWorkData, setNewWorkData] = useState(null);

  useEffect(() => {
    let dataToLoad = null;
    if (isEditMode && workHistoryData) {
      dataToLoad = {
        ...defaultValues,
        ...workHistoryData,
        industry_sector: workHistoryData.organization_details?.industry_sector || workHistoryData.industry_sector || '',
      };
    } else if (isEditMode && !workHistoryData) {
      dataToLoad = null;
    } else {
      const savedDataString = localStorage.getItem('workExperience');
      if (savedDataString) {
        try {
          dataToLoad = JSON.parse(savedDataString);
        } catch (error) {
          localStorage.removeItem('workExperience');
          dataToLoad = null;
        }
      }
    }

    reset(dataToLoad || defaultValues);

    if (dataToLoad) {
      const accomplishments = dataToLoad.key_accomplishments || [];
      setKeyAccomplishments(accomplishments.length > 0 ? [...accomplishments, ''] : ['']);

      const allSkills = dataToLoad.all_skills || [];
      const selectedSkills = dataToLoad.skills || [];
      setChipData(allSkills.length > 0 ? allSkills : selectedSkills);
    } else {
      setKeyAccomplishments(['']);
      setChipData([]);
    }
  }, [isEditMode, workHistoryData, reset]);

  useEffect(() => {
    if (isEditMode) return; // Only save for new entries

    const saveData = (currentFormValues) => {
      const dataToStore = {
        ...currentFormValues,
        key_accomplishments: keyAccomplishments.filter(acc => acc.trim() !== ''),
        all_skills: chipData,
      };

      if (checkLocalDataIsEmpty(dataToStore)) {
        localStorage.removeItem('workExperience');
      } else {
        localStorage.setItem('workExperience', JSON.stringify(dataToStore));
      }
    };

    const subscription = watch((formValues) => {
      saveData(formValues);
    });

    saveData(getValues());

    return () => subscription.unsubscribe();
  }, [watch, isEditMode, keyAccomplishments, chipData, getValues]);

  useEffect(() => {
    /**
     * Fetch industry list from the API.
     * @function
     * @returns {Promise<void>}
     */
    const fetchIndustryList = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_END_POINT}${endpoints?.services?.industry}`);
        const data = await response.json();
        setIndustryList(data || []);
      } catch (error) {
        console.error("Failed to fetch industry list", error);
      }
    };

    fetchIndustryList();
  }, []);

  const handleAccomplishmentChange = (index, value) => {
    const newAccomplishments = [...keyAccomplishments];
    newAccomplishments[index] = value;
    if (index === newAccomplishments.length - 1 && value.trim() !== '') {
      newAccomplishments.push('');
    } else if (!value && newAccomplishments.length > 1 && index !== newAccomplishments.length - 1) {
      newAccomplishments.splice(index, 1);
    }
    setKeyAccomplishments(newAccomplishments);
  };

  const handlePresentDateChange = (event) => {
    const isChecked = event.target.checked;
    if (currentCompany && (isEditMode ? !workHistoryData.present : true)) {
      setErrors(true);
    } else {
      setValue('present', isChecked, { shouldDirty: true });
      if (isChecked) {
        setValue('work_history_to', null, { shouldDirty: true });
        trigger("work_history_to")
      }
    }
  };

  const onSubmit = handleSubmit(async (formData) => {
    if (isEditMode) {
      formData.workId = id;
    }
    if (!validateSkills(selectedChips)) {
      if (!explaination) {
        return;
      }
      setShowFlag('');
    } else {
      setShowFlag('');
    }
    formData.skills = selectedChips;
    formData.all_skills = chipData;
    formData.key_accomplishments = keyAccomplishments.filter((accomplishment) => accomplishment.trim() !== '');
    const { isOverlapping, type, degree } = checkingOverlapping(formData);

    if (formData?.organization_name === 'Work Gap') {
      console.log('Work Gap');
    } else if (isOverlapping) {
      if (!formData.is_overlap) {
        formData.is_overlap = true;
        setNewWorkData(formData);
        setDegree(degree);
        setType(type);
        handleOpenOverlapping();
        return;
      }
    } else {
      formData.is_overlap = false;
    }
    if (showWork) {
      handleShowWork(formData, id);
      handleReset(defaultValues);
      setReadOnly(true);
      return;
    }

    if (formData?.industry_sector) {
      formData.organization_details = {
        ...formData?.organization_details,
        industry_sector: formData?.industry_sector,
      };
    }

    await updateWork(formData);
  });

  /**
   * Updates the work experience of a candidate.
   * @param {Object} data - The work experience data to be updated.
   * @returns {Promise} - A promise that resolves with the response from the server after updating the work experience.
   */
  const updateWork = async (data) => {
    if (data?.industry_sector) {
      data.organization_details = {
        ...data?.organization_details,
        industry_sector: data?.industry_sector,
      };
    }
    if (data) {
      const response = await updateCandidateWorkExperience(data);
      if (response?.success === true && response?.data) {
        const workId = response?.data?._id;
        if (response?.removedWorkGapResult)
          enqueueSnackbar("Career break has been removed.", { variant: 'success' });
        if (!isEditMode) {
          localStorage.removeItem('workExperience');
        }
        if (isEditMode) onWorkExperienceDataChange(true, workId);
        else onWorkExperienceDataChange(true);
        onUploadSuccess();
      } else if (response?.success === false) {
        enqueueSnackbar(response?.message, { variant: 'error' });
      }
    }
  };
  /**
   * Updates the work history "to" date field in the form.
   * @param {Date} date - The new date to set as the "to" date.
   */
  const updateWorkToDate = (date) => {
    if (date) {
      const lastDayOfMonth = date.endOf('month');
      setValue('work_history_to', lastDayOfMonth?.$d);
      trigger('work_history_to');
    }
  };
  /**
   * Updates the work history "from" date field in the form.
   * @param {Date} date - The new date to set as the "from" date.
   */
  const updateWorkFromDate = async (date) => {
    if (date) {
      const firstDayOfMonth = date.startOf('month');
      setFrom(date);
      setValue('work_history_from', firstDayOfMonth?.$d);
      trigger('work_history_from');
    }
  };

  /**
   * Handles the selection of skills. If the label is already selected, it
   * removes it from the list of skills. If the label is not selected and the
   * length of the skills list is less than 8, it adds the label to the list.
   * If the length of the skills list is 8 or more, it shows an error message.
   * @param {string} label - The label of the skill to be selected or deselected.
   */
  const handleClick = (label) => {
    const currentSkills = getValues('skills') || [];
    let newSkills;
    if (currentSkills.includes(label)) {
      newSkills = currentSkills.filter((chip) => chip !== label);
    } else {
      if (currentSkills.length >= 8) {
        setShowFlag('You can select up to 8 skills.');
        return;
      }
      newSkills = [...currentSkills, label];
    }
    setValue("skills", newSkills, { shouldDirty: true });
    trigger("skills");
    validateSkills(newSkills);
  };

  /**
   * Checks if the total number of skills is within the valid range of 3-8.
   * Sets an error flag if the number of skills is out of range.
   * @param {number} totalSkillls - The total number of skills.
   * @returns {boolean} True if the number of skills is valid, false otherwise.
   */
  const validateSkills = (totalSkillls) => {
    if (totalSkillls?.length < 3) {
      setShowFlag('Please select at least 3 skills');
      return false;
    } else if (totalSkillls?.length > 8) {
      setShowFlag('You can select up to 8 skills.');
      return false;
    }
    setShowFlag('');
    return true;
  };

  /**
   * Opens the OTP verification dialog.
   */
  // const handleOpenOtpDialog = () => {
  //   setOpen(true);
  // };

  /**
   * Closes the OTP verification dialog and remove work experience dialog.
   */
  // const handleClose = () => {
  // setOpen(false);
  //   setOpenRemove(false);
  // };
  /**
   * Handles the selection of skills. If the label is already selected, it
   * removes it from the list of skills. If the label is not selected and the
   * length of the skills list is less than 8, it adds the label to the list.
   * If the length of the skills list is 8 or more, it shows an error message.
   * @param {Array<string>} moreSkills - The new set of skills to add to the list of skills.
   */
  const handleAddMoreSkills = (moreSkills) => {
    validateSkills(moreSkills);
    if (moreSkills?.length > 0) {
      setValue("skills", moreSkills, { shouldDirty: true });
      setValue("all_skills", moreSkills, { shouldDirty: true });
      trigger("skills");
      setChipData(moreSkills);
    }
  };

  /**
   * Handles changes to the 'gap_explaination' field in the form.
   * @param {React.ChangeEvent<HTMLTextAreaElement>} event - The event object from the 'change' event.
   */
  const handleChangeGapExplaination = (event) => {
    setValue('gap_explaination', event.target.value);
  }
  /**
   * Handles the deletion of a work experience item.
   * If showWork is true, it calls handleDeleteEdu with the workHistoryData's id and closes the dialog.
   * If showWork is false, it calls deleteWorkHistory with the workHistoryData's id and updates the work experience data if the deletion is successful.
   * If the deletion fails, it shows an error message.
   * In any case, it closes the dialog.
   */
  const handleDeleteAction = async () => {
    if (showWork) {
      handleDeleteEdu(workHistoryData?._id);
      handleCloseRemove();
      return;
    }
    const response = await deleteWorkHistory({ workId: workHistoryData._id });
    if (response && response?.success) {
      onWorkExperienceDataChange(true);
    } else {
      enqueueSnackbar(response.message, { variant: 'error' });
    }
    handleCloseRemove();
  };
  /**
   * Handles the deletion of a work experience entry.
   * - Stops the event propagation.
   * - If the user is at stage 3 or above, and there's only one work experience
   *   entry left (which isn't a "Work Gap"), a snackbar error message is shown
   *   indicating the field cannot be empty.
   * - If workId is not provided, removes 'workExperience' from local storage
   *   and calls the onCancel function.
   * - If workId is provided, opens the remove confirmation dialog.
   * 
   * @param {string} workId - The ID of the work experience to be deleted.
   * @param {string} title - The title of the work experience.
   * @param {Object} event - The event object.
   */

  const handleDeleteWork = async (workId, title, event) => {
    event.stopPropagation();
    if (user?.stages >= 3 && workHistoryLength === 1 && title !== 'Work Gap') {
      enqueueSnackbar('The Work Experience field cannot be empty', { variant: 'error' });
      return;
    }
    if (!workId) {
      localStorage.removeItem('workExperience');
      onCancel();
    } else {
      handleOpenRemove();
    }
  };
  /**
   * Closes the overlapping dialog box.
   * @function
   */
  // const handleCloseOverlapping = () => {
  //   setOpenOverlapping(false);
  // };
  /**
   * Handles the confirmation of a work experience entry.
   * - If the user is in 'showWork' mode, calls the handleShowWork function with the new work data
   *   and sets the component to read-only.
   * - If the user is not in 'showWork' mode, calls the updateWork function with the new work data.
   * @param {Object} newWorkData - The new work experience data.
   */
  const handleConfirm = async (newWorkData) => {
    if (showWork) {
      handleShowWork(newWorkData, id);
      setReadOnly(true);
      return;
    }
    await updateWork(newWorkData);
  };

  /**
   * A component that renders an edit icon.
   * @param {{ margin: boolean }} props - The props of the component.
   * @returns {ReactElement} The edit icon component.
   */
  const EditIcon = ({ margin }) => {
    return (
      <Tooltip title="Data updated in this section will be reflected across all resumes" placement="top" arrow>
        <IconButton
          aria-label="toggle password visibility"
          edge="end"
          disableRipple
          sx={{ marginRight: margin ? "2px" : "" }}
        >
          <SvgIcon sx={{ width: '16px', height: '16px' }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M12.2907 16.0002H2.10143C0.944657 16.0002 0.00354004 15.0592 0.00354004 13.9023V3.71301C0.00354004 2.55623 0.944542 1.61511 2.10143 1.61511H6.77058C7.10174 1.61511 7.36995 1.88332 7.36995 2.21448C7.36995 2.54564 7.10174 2.81385 6.77058 2.81385H2.10143C1.60546 2.81385 1.20238 3.21694 1.20238 3.71291V13.9022C1.20238 14.3982 1.60546 14.8013 2.10143 14.8013H12.2907C12.7867 14.8013 13.1898 14.3982 13.1898 13.9022V9.23306C13.1898 8.9019 13.458 8.63369 13.7892 8.63369C14.1203 8.63369 14.3885 8.9019 14.3885 9.23306V13.9022C14.3885 15.059 13.4476 16.0002 12.2907 16.0002Z" fill="#1575C6" />
              <path d="M4.44409 9.0085L11.0013 2.45129L13.5478 4.9978L6.9906 11.555L4.44409 9.0085Z" fill="#1575C6" />
              <path d="M15.7117 1.43961L14.5609 0.28882C14.1758 -0.0962733 13.551 -0.0962733 13.1651 0.28882L11.8495 1.60444L14.396 4.15095L15.7116 2.83533C16.0967 2.45024 16.0968 1.82545 15.7117 1.43961Z" fill="#1575C6" />
              <path d="M3.6757 9.93518L3.08308 12.5229C3.02914 12.7597 3.24042 12.9709 3.47717 12.917L6.06492 12.3244L3.6757 9.93518Z" fill="#1575C6" />
            </svg>
          </SvgIcon>
        </IconButton>
      </Tooltip>
    )
  };
  /**
   * Handles the cancel action of the add/edit work experience form.
   * If no work experience ID is provided, it removes the work experience data from local storage.
   * In any case, it calls the onCancel function to close the form.
   */
  const handleCancel = () => {
    if (!id) {
      localStorage.removeItem('workExperience');
      reset(defaultValues);
      setKeyAccomplishments(['']);
      setChipData([]);
    }
    onCancel();
  };

  const handleCollapse = () => {
    if (isEditMode) {
      const originalData = {
        ...defaultValues,
        ...workHistoryData,
        industry_sector: workHistoryData?.organization_details?.industry_sector || workHistoryData?.industry_sector || '',
      };
      reset(originalData);

      const accomplishments = originalData.key_accomplishments || [];
      setKeyAccomplishments(accomplishments.length > 0 ? [...accomplishments, ''] : ['']);
      const allSkills = originalData.all_skills || [];
      const selectedSkills = originalData.skills || [];
      setChipData(allSkills.length > 0 ? allSkills : selectedSkills);
    }
    handleArrowClick();
  };

  const { openDialog, closeDialog, dialogLocation } = useBackNavigationDialog();
  const dialogIds = dialogLocation.state?.dialogIds || [];

  const openSkillDrawer = dialogIds.includes("moreSkills");
  const openOverlapping = dialogIds.includes("overlapping");
  const openRemove = dialogIds.includes("removeExpandWorkExperience");

  const handleCloseSkillDrawer = () => {
    closeDialog('moreSkills');
  };
  const handleOpenSkillDrawer = () => {
    openDialog('moreSkills');
  };
  const handleCloseOverlapping = () => {
    closeDialog('overlapping');
  };
  const handleOpenOverlapping = () => {
    openDialog('overlapping');
  };
  const handleCloseRemove = () => {
    closeDialog('removeExpandWorkExperience');
  };
  const handleOpenRemove = () => {
    openDialog('removeExpandWorkExperience');
  };

  if (!roleData || !roleData?.length > 0) {
    return (
      <div key="add_edit_edu">
        <Box className="candidate-basic-det-wrapper"></Box>
        <Card className="form-wrapper" variant="outlined">
          <CardContent>
            <Stack direction="row" justifyContent={'space-between'} alignItems={'center'} mb={3}>
              <CircularProgress />
            </Stack>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <Box className="candidate-basic-det-wrapper work-exp-details-form">
      <FormProvider methods={methods} onSubmit={onSubmit}>
        <Tooltip title={isReadOnly ? "Any information updated after unlocking will be reflected across resumes" : ''} arrow followCursor placement='top'>
          <Box className="job-update-form-top" mb={1}>
            {!isMobile && <Box className="details-info-header" mb={2.5}>
              <Stack direction="row" justifyContent={'space-between'} spacing={2}>
                <Box>
                  <Stack direction="row" alignItems={'center'} spacing={1.5}>
                    <Typography variant="h6" className="card-header-title">
                      {watch('organization_name') === 'Work Gap'
                        ? (watch('gap_title') ? `CAREER BREAK: ${allCapitalize(watch('gap_title'))}` : 'CAREER BREAK')
                        : (watch('highest_designation') ? (`${watch('highest_designation')} `) + 'at ' : '') +
                        (watch('organization_name') ? allCapitalize(`${watch('organization_name')}`) : 'Work Experience Details')
                      }
                    </Typography>
                  </Stack>
                </Box>
                <Stack direction="row" alignItems={'center'} spacing={2}>
                  {workHistoryData && !isMobile &&
                    (!isReadOnly ?
                      (workHistoryData?.organization_name !== "Work Gap" && <IconButton
                        className="delete-sec-btn"
                        aria-label="delete"
                        onClick={(event) => handleDeleteWork(workHistoryData?._id, workHistoryData?.organization_name, event)}
                      >
                        <DeleteSvg />
                      </IconButton>) :
                      <IconButton aria-label="delete" sx={{ padding: 0, marginRight: 1 }} className="edit-icon" disableRipple onClick={() => setReadOnly(false)}>
                        <EditSvgIcon />
                      </IconButton>)
                  }
                  {!isMobile && <KeyboardArrowUpOutlinedIcon
                    onClick={handleCollapse}
                    style={{ cursor: 'pointer' }} />}
                </Stack>
              </Stack>
            </Box>
            }

            <Box className="details-info-content">
              {(workHistoryData?.organization_name !== "Work Gap") && <>
                <Grid container spacing={2} mb={3}>
                  <Grid item xs={12} md={6}>
                    {/* company name */}
                    <FormControl fullWidth size="small">
                      <Controller
                        control={control}
                        name="organization_name"
                        defaultValue={toTitleCase(defaultValues?.organization_name) || ''}
                        render={({ field }) => {
                          const handleCompanyChange = (event, newValue) => {
                            if (typeof newValue === 'object' && newValue?.name) {
                              // User selected a company object from the list
                              field.onChange(toTitleCase(newValue.name)); // Update the primary field
                              setValue("organization_details", newValue, { shouldDirty: true });
                              setValue("industry_sector", newValue.industry_sector || '', { shouldDirty: true });
                            } else if (typeof newValue === 'string') {
                              // User typed a new company name (freeSolo)
                              field.onChange(toTitleCase(newValue)); // Update the primary field
                              setValue("organization_details", {}, { shouldDirty: true });
                              setValue("industry_sector", '', { shouldDirty: true });
                            } else {
                              // Handle clear event
                              field.onChange('');
                              setValue("organization_details", null, { shouldDirty: true });
                              setValue("industry_sector", '', { shouldDirty: true });
                            }
                          };

                          return (
                            <TypeAhead
                              {...field}
                              ref={focusRef}
                              api={`${process.env.REACT_APP_END_POINT}${endpoints?.services?.company}`}
                              fullWidth
                              label="Company"
                              placeholder="Enter your company name"
                              displayTextKey="name"
                              freeSolo={true}
                              onChange={handleCompanyChange}
                              onInputChange={(e, value) => field.onChange(value)}
                              inputProps={{
                                style: { textTransform: 'capitalize' }
                              }}
                              disabled={isReadOnly}
                            />
                          )
                        }}
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    {/* designation */}
                    <FormControl fullWidth size="small">
                      <Controller
                        control={control}
                        name="highest_designation"
                        defaultValue={defaultValues?.highest_designation || []}
                        render={({ field }) => (
                          <TextField
                            size="small"
                            {...field}
                            // {...register('highest_designation')}
                            label="Designation"
                            variant="outlined"
                            fullWidth
                            error={!!errors.highest_designation}
                            helperText={errors.highest_designation ? errors.highest_designation.message : ''}
                            // onChange={}
                            disabled={isReadOnly}
                          />
                        )}
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    {/* job function */}
                    <FormControl fullWidth size="small">
                      <Controller
                        control={control}
                        name="last_job_function"
                        defaultValue={defaultValues?.last_job_function || ''}
                        render={({ field }) => (
                          <TypeAhead
                            {...field}
                            data={roleData}
                            label="Job Function"
                            size="small"
                            displayTextKey="name"
                            onChange={(e, value) => {
                              field.onChange(value?.display_name || value);
                              if (value?.skills) {
                                setValue('all_skills', value.skills, { shouldDirty: true });
                                setValue('skills', [], { shouldDirty: true });
                                setChipData(value.skills);
                              }
                            }}
                            disabled={isReadOnly}
                          />
                        )}
                      />
                    </FormControl>
                  </Grid>
                  {/* job type */}
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="job_type">Job Type</InputLabel>
                      <Controller
                        control={control}
                        name="job_type"
                        defaultValue={defaultValues?.job_type || ''}
                        render={({ field }) => (
                          <Select
                            {...field}
                            {...register('job_type')}
                            labelId="job_type"
                            error={!!errors.job_type}
                            helperText={errors.job_type?.message}
                            label="Job Type"
                            variant="outlined"
                            fullWidth
                            disabled={isReadOnly}
                          >
                            {jobType?.map((item) =>
                              <MenuItem value={item?.label}>{item?.label}</MenuItem>
                            )}
                          </Select>
                        )}
                      />
                      <Typography variant="caption" color="error">
                        {errors.job_type?.message}
                      </Typography>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    {/* The Controller from react-hook-form remains the same */}
                    {industryList.length === 0 && isEditMode ? (
                      <TextField
                        label="Industry"
                        size="small"
                        disabled
                        value="Loading..."
                        fullWidth
                      />
                    ) : (<Controller
                      control={control}
                      name="industry_sector"
                      defaultValue={defaultValues?.organization_details?.industry_sector || ''}
                      render={({ field }) => (
                        <Autocomplete
                          options={industryList}
                          getOptionLabel={(option) => option.name || ''}
                          isOptionEqualToValue={(option, value) => option._id === value._id}
                          value={industryList.find(item => (item.name).toLowerCase() === (field.value).toLowerCase()) || null}
                          onChange={(event, newValue) => {
                            field.onChange(newValue ? newValue.name : '');
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Industry"
                              size="small"
                              error={!!errors?.industry_sector}
                            />
                          )}
                          disabled={isReadOnly}
                          fullWidth
                        />
                      )}
                    />)}
                    {errors?.industry_sector && (
                      <Typography variant="caption" color="error" sx={{ ml: 1 }}>
                        {errors?.industry_sector.message}
                      </Typography>
                    )}
                  </Grid>
                  {/* location */}
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth size="small">
                      <Controller
                        control={control}
                        name="work_history_location"
                        defaultValue={defaultValues?.work_history_location || ''}
                        render={({ field }) => (
                          <TypeAhead
                            {...field}
                            // {...register('work_history_location')}
                            api={`${process.env.REACT_APP_END_POINT}${endpoints?.services?.location}`}
                            label="Location"
                            onChange={(e, value) => {
                              setValue("work_history_location", value, { shouldDirty: true });
                            }}
                            disabled={isReadOnly}
                          />
                        )}
                      />
                    </FormControl>
                  </Grid>
                  {/* team size managed */}
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth size="small" error={!!errors?.team_size_managed}>
                      <InputLabel id="team_size_managed">People Managed (Direct Reportees)</InputLabel>
                      <Controller
                        control={control}
                        name="team_size_managed"
                        defaultValue={defaultValues?.team_size_managed || ''}
                        render={({ field }) => (
                          <Select
                            {...field}
                            // {...register('team_size_managed')}
                            labelId="team_size_managed"
                            label="People Managed (Direct Reportees)"
                            variant="outlined"
                            fullWidth
                            disabled={isReadOnly}
                          >
                            <MenuItem value="Individual contributor (IC)">Individual contributor (IC)</MenuItem>
                            <MenuItem value="1-4">1-4</MenuItem>
                            <MenuItem value="5-10">5-10</MenuItem>
                            <MenuItem value="11-25">11-25</MenuItem>
                            <MenuItem value="26-50">26-50</MenuItem>
                            <MenuItem value="51-100">51-100</MenuItem>
                            <MenuItem value="100+">100+</MenuItem>
                          </Select>
                        )}
                      />
                      {errors?.team_size_managed && <FormHelperText error>{errors?.team_size_managed?.message}</FormHelperText>}
                    </FormControl>
                  </Grid>

                </Grid>
                <Box mb={2}>
                  <Card className="disability-card" variant="outlined" sx={{ background: isReadOnly ? '#F1F5F7 !important' : '#E7EBFD !important' }}>
                    <CardContent sx={{ background: isReadOnly ? '#F1F5F7 !important' : '#E7EBFD !important' }}>
                      <FormControlLabel
                        control={
                          <Stack direction="row" spacing={1} alignItems="start">
                            <AntSwitch
                              checked={watch('present')}
                              onChange={handlePresentDateChange}
                              inputProps={{ 'aria-label': 'ant design' }}
                              disabled={isReadOnly}
                            />
                            <Box>
                              <Typography className="heading" variant="p" gutterBottom>
                                You are currently working with {watch('organization_name') ? allCapitalize(watch('organization_name')) : ' this company'} as{' '}
                                {watch('highest_designation') ? watch('highest_designation') : ' your current role'}
                              </Typography>
                              <Typography className="info" variant="body2" gutterBottom>
                                Please select this if you are still working in this company & current role
                              </Typography>
                            </Box>
                          </Stack>
                        }
                      />
                    </CardContent>
                  </Card>
                  {error && (
                    <Typography className="error-text" component="p" style={{ color: 'red' }}>
                      You are currently working with {currentCompany}.
                    </Typography>
                  )}
                </Box>
              </>
              }
              <Grid container spacing={2} mb={3}>
                {(workHistoryData?.organization_name !== "Work Gap") && <>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <Controller
                        control={control}
                        name="work_history_from"
                        defaultValue={defaultValues?.work_history_from}
                        render={({ field }) => (
                          <DatePicker
                            {...field}
                            id="work_history_from"
                            // {...register('work_history_from')}
                            format="MM/YYYY"
                            views={['year', 'month']}
                            disableFuture
                            label="Start Date"
                            sx={{ width: '100%' }}
                            value={dayjs(field?.value)}
                            onChange={(date) => updateWorkFromDate(date)}
                            slotProps={{
                              textField: {
                                size: 'small',
                                error: !!errors?.work_history_from,
                                helperText: errors?.work_history_from?.message,
                              },
                            }}
                            disabled={isReadOnly}
                          />
                        )}
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <Controller
                        control={control}
                        name="work_history_to"
                        defaultValue={defaultValues?.work_history_to}
                        render={({ field }) => (
                          <DatePicker
                            {...field}
                            id="work_history_to"
                            format="MM/YYYY"
                            views={['year', 'month']}
                            disableFuture
                            sx={{ width: '100%' }}
                            disabled={present || isReadOnly}
                            label="End Date"
                            value={dayjs(field?.value)}
                            onChange={(date) => updateWorkToDate(date)}
                            slotProps={{
                              textField: {
                                size: 'small',
                                error: !!errors?.work_history_to,
                                helperText: errors?.work_history_to?.message,
                              },
                            }}
                          />
                        )}
                      />
                    </FormControl>
                  </Grid>
                </>
                }
                <Grid item xs={12} md={12}>
                  {workHistoryData?.organization_name == "Work Gap" && (
                    <>
                      <Typography className="info-wrapper" variant="body2" gutterBottom mb={3} sx={{ marginTop: '-20px' }}>
                        {workHistoryData.work_history_from ? `${dayjs(workHistoryData.work_history_from).format('MMM YYYY')} - ${workHistoryData.work_history_to && dayjs(workHistoryData.work_history_to).format('MMM YYYY')}` : ''}
                      </Typography>
                      <Grid item xs={12} spacing={2} mb={3}>
                        <FormControl fullWidth sx={{ marginTop: showWork && '16px!important' }}>
                          <Controller
                            control={control}
                            name="gap_title"
                            defaultValue={defaultValues?.gap_title || ''}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                label="Title"
                                error={!!errors.gap_title}
                                helperText={errors.gap_title ? errors.gap_title?.message : ''}
                                size='small'
                                onChange={(e) => {
                                  const inputValue = e.target.value;
                                  if (inputValue?.length <= 50) {
                                    setValue('gap_title', inputValue);
                                    clearErrors('gap_title');
                                  } else {
                                    setError('gap_title', { message: "The title must not exceed 50 characters" });
                                  }
                                }}
                                disabled={isReadOnly}
                              />
                            )}
                          />
                        </FormControl>
                      </Grid>
                      <Grid item xs={12}>
                        <FormControl fullWidth>
                          <Controller
                            control={control}
                            name="gap_explaination"
                            defaultValue={defaultValues?.gap_explaination || ''}
                            render={({ field }) => (
                              <TextField
                                multiline
                                {...field}
                                label="Reason"
                                error={!!errors.gap_explaination}
                                helperText={errors.gap_explaination?.message || `${getValues('gap_explaination') ? getValues('gap_explaination')?.trim()?.split(/\s+/)?.length || 0 : 0} / 25 words`}
                                size='small'
                                onChange={(e) => {
                                  const inputValue = e.target.value;
                                  const words = inputValue.trim().split(/\s+/);
                                  if (words?.length <= 25) {
                                    setValue('gap_explaination', inputValue);
                                    clearErrors('gap_explaination');
                                  } else {
                                    setError('gap_explaination', { message: "Maximum 25 words allowed." });
                                  }
                                }}
                                disabled={isReadOnly}
                              />
                            )}
                          />
                        </FormControl>
                        <FormControlLabel sx={{ fontSize: '14px' }}
                          control={
                            <Checkbox
                              {...methods.register(`show_in_resume `)}
                              checked={watch('show_in_resume')}
                              onChange={(e) => setValue('show_in_resume', e.target.checked)}
                              defaultChecked={false}
                              id="show_in_resume "
                              name="show_in_resume "
                              disabled={isReadOnly}
                            />
                          }
                          label="Show this break in my resume"
                        />
                      </Grid>
                    </>
                  )}
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Tooltip>
        {(workHistoryData?.organization_name !== "Work Gap") && <>
          <Tooltip title={isReadOnly ? "Any information updated after unlocking will be reflected across resumes" : ''} arrow followCursor placement='top'>
            <Box className="job-update-form-top" mb={1} sx={{ padding: isReadOnly && !isMobile && '0px 20px !important' }}>
              <Box className="details-info-content">
                <Box className="besic-det-skills-wrapper">
                  <Box mb={2}>
                    <Typography className="title" variant="body2" gutterBottom>
                      Skills
                    </Typography>
                    <Typography className="info-txt" variant="body2" gutterBottom>
                      Minimum 3 and maximum 8 skills to be selected.
                    </Typography>
                  </Box>
                  <Stack direction="row" flexWrap="wrap">
                    {chipData?.map((label, index) => {
                      const selectedSkills = watch('skills', []);
                      const isSelected = selectedSkills.some(
                        (skill) => skill.toLowerCase() === label.toLowerCase()
                      );

                      return (
                        <Chip
                          key={index}
                          label={label}
                          size="small"
                          clickable
                          variant={isSelected ? 'filled' : 'outlined'}
                          style={{
                            color: isSelected ? 'white' : 'black',
                            backgroundColor: isSelected ? '#ade9ab' : 'transparent',
                            border: isSelected ? 'none' : '1px solid rgba(0, 0, 0, 0.23)',
                          }}
                          onClick={() => handleClick(label)}
                          tabIndex={-1}
                          disabled={isReadOnly}
                        />
                      );
                    })}
                  </Stack>

                  {(errors?.skills || showFlag) && (
                    <Typography variant="caption" color="error" style={{ color: 'red', fontSize: 'small' }}>
                      {errors.skills?.message || showFlag}<br />
                    </Typography>
                  )}
                  {!isReadOnly && <Link className="add-more-skills-link" underline="none" size="small" onClick={handleOpenSkillDrawer} tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleOpenSkillDrawer();
                      }
                    }}
                  >
                    Add More Skills
                  </Link>}
                </Box>
              </Box>
            </Box>
          </Tooltip>

          <Box className={isReadOnly ? "job-update-form-bottom" : "job-update-form-top"}>
            <Box className="accomplishments">
              <Typography className="title" variant="body2" gutterBottom>
                Accomplishments
              </Typography>
              <Typography className="info-txt" variant="body2" gutterBottom>
                Well structured accomplishments help recruiters get visibility on your strengths
              </Typography>

              {keyAccomplishments?.map((accomplishment, index) => (
                <Box key={index} mt={2}>
                  <FormControl fullWidth>
                    <DynamicTextField
                      label="Key Accomplishments"
                      rowsMax={10}
                      value={accomplishment}
                      onChange={(value) => handleAccomplishmentChange(index, value)}
                    />
                  </FormControl>
                </Box>
              ))}
            </Box>
          </Box>
        </>
        }
        <Box className="job-update-form-top">
          <Box className="details-info-header" mb={isReadOnly && 2.5}>
            <Box className="form-action-btn">
              <Stack direction="row" spacing={{ xs: 0, md: 2 }} mt={{ xs: 0, md: isReadOnly ? 0 : 3 }}>
                <Button variant="text" className="cancel-btn" sx={{ display: { xs: 'none', md: 'block' } }} size="large" onClick={handleCancel} >Cancel</Button>
                <Button variant="contained" className="done-btn" size="large" type="submit" disabled={isSubmitting}>
                  {isEditMode ? (isSubmitting ? 'Updating...' : 'Done') : (isSubmitting ? 'Saving...' : 'Done')}
                </Button>
              </Stack>
            </Box>
          </Box>
        </Box>
        {/* } */}
        {isMobile ?
          <Drawer
            anchor="bottom"
            open={openSkillDrawer}
            onClose={handleCloseSkillDrawer}
            className="forgot-password-dialog otp-dialog-wrapper"
          >
            <AddSkills
              onClose={handleCloseSkillDrawer}
              skills={getValues('all_skills')}
              selectedskills={getValues('skills')}
              onAddSkills={handleAddMoreSkills}
            />
          </Drawer> :
          <Dialog
            open={openSkillDrawer}
            onClose={handleCloseSkillDrawer}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            className="otp-dialog-wrapper"
          >
            <AddSkills
              onClose={handleCloseSkillDrawer}
              skills={getValues('all_skills')}
              selectedskills={getValues('skills')}
              onAddSkills={handleAddMoreSkills}
            />
          </Dialog>}
        {isMobile ?
          <Drawer
            anchor="bottom"
            open={openRemove}
            onClose={handleCloseRemove}
            className="forgot-password-dialog otp-dialog-wrapper"
          >
            <RemoveWorkExperience onClose={handleCloseRemove} onRemove={handleDeleteAction} title={workHistoryData?.organization_name} />
          </Drawer> :
          <Dialog
            open={openRemove}
            onClose={handleCloseRemove}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            className="otp-dialog-wrapper"
          >
            <RemoveWorkExperience onClose={handleCloseRemove} onRemove={handleDeleteAction} title={workHistoryData?.organization_name} />
          </Dialog>}
        {isMobile ?
          <Drawer
            anchor="bottom"
            open={openOverlapping}
            onClose={handleCloseOverlapping}
            className="forgot-password-dialog otp-dialog-wrapper"
          >
            <Overlapping onClose={() => handleCloseOverlapping()}
              onConfirm={() => handleConfirm(newWorkData)}
              type={type}
              degree={degree} />
          </Drawer> :
          <Dialog
            open={openOverlapping}
            onClose={handleCloseOverlapping}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            className="otp-dialog-wrapper"
          >
            <Overlapping onClose={() => handleCloseOverlapping()}
              onConfirm={() => handleConfirm(newWorkData)}
              type={type}
              degree={degree} />
          </Dialog>}
      </FormProvider>
    </Box>
  );
};

export default AddWorkExperience;
